import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ImageUpload } from '@/components/ImageUpload';
import { usePostUniformGalleryMutation } from '@/services/queries/uniform-gallery';
import { PostMediaUniformGalleryRequest } from '@/services/request/uniform-gallery';
import { SelectField } from '@/components/SelectField';
import { UniformSeason } from '@/model';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface UniformGalleryCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  seasons: UniformSeason[] | undefined;
  onClose: () => void;
  onDone?: () => void;
}

export const UniformGalleryCreateModal = ({ isOpen, seasons, onClose, onDone }: UniformGalleryCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postItemMutation = usePostUniformGalleryMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: '',
      image: undefined,
      season_uuid: '',
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PostMediaUniformGalleryRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        image: data.image,
        title: data.title,
        credit: data.credit,
        season_uuid: data.season_uuid,
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) ||
              t('pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t(
                'pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_team_label',
              )}
              placeholder={t(
                'pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_team_label',
              )}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputGroup display='flex' flexDirection='column'>
              <Text fontWeight='medium'>
                {t(
                  'pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_season_label',
                )}
              </Text>
              <SelectField
                defaultValue=''
                {...register('season_uuid')}
                rootProps={{ isInvalid: !!errors?.season_uuid, errorMessage: errors?.season_uuid?.message, mt: 0 }}
              >
                <option value='' disabled>
                  {t(
                    'pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_season_default_option',
                  )}
                </option>
                {seasons?.map(item => (
                  <option key={item.uuid} value={item.uuid}>
                    {item.title}
                  </option>
                ))}
              </SelectField>
            </InputGroup>
            <InputField
              withoutFloating
              label={t(
                'pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_title_label',
              )}
              {...register('title')}
              helperText={t(
                'pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_title_helper_text',
              )}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <InputField
              withoutFloating
              label={t(
                'pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_credit_label',
              )}
              {...register('credit')}
              maxLength={50}
              rootProps={{ isInvalid: !!errors?.credit, errorMessage: errors?.credit?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t(
                  'pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_image_label',
                )}
                imageURL={''}
                onFileChange={file => setValue('image', file)}
                helpText={t(
                  'pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_image_helper_text',
                )}
                error={errors?.image?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t(
                  'pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_cancel_button',
                )}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t(
                  'pages.application_uniform_gallery.components.uniform_gallery_create_modal.uniform_gallery_create_submit_button',
                )}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
